import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

/**
 * Keeps selected routes alive across navigation instead of destroying them.
 *
 * Any route carrying `data: { reuseKey: '<key>' }` is detached (component +
 * DOM preserved) when the user navigates away, and re-attached on return — so
 * its state survives. The dashboard route family uses this to keep the live
 * `<tableau-viz>` element (and the user's filters) intact when leaving the
 * dashboard for another hub and coming back. Every other route falls through
 * to Angular's default behaviour.
 */
@Injectable()
export class AppRouteReuseStrategy implements RouteReuseStrategy {
  private handlers = new Map<string, DetachedRouteHandle>();

  /** Stable reuse key for a route, or null when the route should not be reused. */
  private reuseKey(route: ActivatedRouteSnapshot): string | null {
    return (route.routeConfig?.data?.['reuseKey'] as string | undefined) ?? null;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.reuseKey(route) !== null;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.reuseKey(route);
    if (!key) return;
    if (handle) {
      this.handlers.set(key, handle);
    } else {
      this.handlers.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.reuseKey(route);
    return key !== null && this.handlers.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.reuseKey(route);
    return key ? this.handlers.get(key) ?? null : null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    // Default Angular behaviour for everything else (also reuses the dashboard
    // component when only the :dashboardid param changes).
    return future.routeConfig === curr.routeConfig;
  }
}
